import { Injectable, NgZone, inject } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom, timeout, of } from 'rxjs';
import { ToolRegistryService, nativePromise } from './tool-registry.service';
import { EntityPageBridgeService } from './entity-page-bridge.service';
import { ToolAnswer, ApprovalAsk, ApprovalAnswer, ToolCall, ZavoBridge } from './mcp-types';
import { locate, makeWhereAmITool, PageMode, screenFromUrl, WhereAmIProbes } from './tools/where-am-i.tool';
import { makeReadProjectTool } from './tools/read-project.tool';
import { makeNavigateTool, NavigateDeps, KnownScreen, NO_PROJECT_REFUSAL } from './tools/navigate.tool';
import { makeOpenProjectTool } from './tools/open-project.tool';
import { makeReadScreenTool } from './tools/read-screen.tool';
import { ActionBehaviours, makeDoActionTool } from './tools/do-action.tool';
import { FAST_LANE_BEHAVIOURS } from './actions/fast-lane';
import { makeShowTool, ShowDeps } from './tools/show.tool';
import { ShowHighlightNgService } from './tools/show-highlight-ng.service';
import { ConfirmWaitsNgService } from './actions/confirm-waits-ng.service';
import { makeEntityConfirmBehaviours } from './actions/entity-confirm';
import { makeOtherConfirmBehaviours } from './actions/other-confirm';
import { makeWorkspaceFormBehaviours } from './actions/workspace-forms';
import { OverlayConfirmationsNgService } from './actions/overlay-confirmations-ng.service';
import { findTargetElement } from './tools/show-highlight-overlay.component';
import { makeAddEntityFieldTool, AddEntityFieldDeps } from './tools/add-entity-field.tool';
import { EntityService } from '../entity.service';
import type { Entity } from '../../model/entity.model';
import { countUnsavedChanges } from '../../dashboard/entity/zs-entity/apply/unsaved-count';
import { PageService } from '../page.service';
import { WorkspacesService } from '../workspaces.service';
import { AuthService } from '../auth.service';
import { StudioNavService } from '../../studio-nav/studio-nav.service';
import type { NavKind } from '../../studio-nav/destinations';
import { type ScreenPanels, ScreenPanelsService } from '../../studio-nav/screen-panels.service';
import { environment } from '../../../environments/environment';
import { UiSharedServiceService } from '../../dashboard/ui-editor/ui-shared-service.service';
import { ApprovalService } from '../../shared/approval-layer/approval.service';
import { SERVER_INSTRUCTIONS } from './server-instructions';
import { inputProblem, withoutCallFields } from './input-check';

export { NO_PROJECT_REFUSAL };
// Was (2026-10-30), before open_project: navigate.tool.ts owns the wording now, and it names that tool.
// // No tool can open a project, so every one of them stops here when none is open.
// export const NO_PROJECT_REFUSAL = 'No project is open in Zavo. Ask the builder to open one; no tool can open a project.';

/** How each row of the action table runs, by action id. A row with none is refused as not built. */
const ACTION_BEHAVIOURS: ActionBehaviours = {
  ...FAST_LANE_BEHAVIOURS,
};

const firstListOrEmpty = <T>(src: { $values: import('rxjs').Observable<T[]> }): Promise<T[]> =>
  firstValueFrom(src.$values.pipe(timeout({ first: 2000, with: () => of([] as T[]) })));

// The canvas tags each leaf component at any depth of nested sections, and never a section itself.
function collectComponentIds(node: { components?: any[] }, ids: string[]): void {
  for (const c of node.components || []) {
    if (c.type === 'section') collectComponentIds(c, ids);
    else if (c.id) ids.push(c.id);
  }
}

// Was (2026-10-30), for the five screens navigate knew then:
// const SCREEN_TO_NAV: Record<KnownScreen, NavKind> = {
//   home: 'projects',
//   pages: 'pages',
//   'page-editor': 'page',
//   entity: 'entity',
//   workspaces: 'workspaces',
// };

const SCREEN_TO_NAV: Partial<Record<KnownScreen, NavKind>> = {
  'select-project': 'projects',
  data: 'data',
  entity: 'entity',
  pages: 'pages',
  'page-preview': 'page-preview',
  'page-spotlight': 'page',
  'page-data': 'page-data',
  'function-editor': 'page-functions',
  team: 'team',
  workspaces: 'workspaces',
  // Was (2026-10-30): schema: 'schema', until Claude stopped reaching this screen.
  'chart-style': 'chart-style',
  settings: 'settings',
};

const OBJECT_KIND: Partial<Record<KnownScreen, 'entity' | 'page'>> = {
  entity: 'entity',
  'page-preview': 'page',
  'page-spotlight': 'page',
  'page-data': 'page',
  'function-editor': 'page',
};

// PageService's stand-in once its selection is reset; a reload or a project switch leaves this and no page.
const NO_PAGE_ID = 'Strange_123WWD';

/** Long enough for a screen just opened to draw, including a Settings card that waits on the access level. */
const PANEL_WAIT_MS = 4000;
/** A project's lists normally land in a second or two. Past this it is better to answer and say they are still coming. */
const LISTS_WAIT_MS = 10_000;

interface Arrival { arrived: Promise<void>; arrive: () => void }
function newArrival(): Arrival {
  let arrive = () => {};
  const arrived = new Promise<void>((resolve) => { arrive = resolve; });
  return { arrived, arrive };
}

@Injectable({ providedIn: 'root' })
export class McpWiringService {
  private registry = inject(ToolRegistryService);
  private entityPageBridge = inject(EntityPageBridgeService);
  private entityService = inject(EntityService);
  private pageService = inject(PageService);
  private workspacesService = inject(WorkspacesService);
  private nav = inject(StudioNavService);
  private router = inject(Router);
  private highlightService = inject(ShowHighlightNgService);
  private confirmWaits = inject(ConfirmWaitsNgService);
  private overlayConfirmations = inject(OverlayConfirmationsNgService);
  private approval = inject(ApprovalService);
  private zone = inject(NgZone);
  private auth = inject(AuthService);
  private uiShared = inject(UiSharedServiceService);
  private panels = inject(ScreenPanelsService);

  private wired = false;
  private cachedWorkspaces: { id: string; name: string }[] = [];
  private cachedPages: { id: string; name: string }[] = [];
  private cachedEntities: Entity[] = [];
  /** Bumped on every sign-out and project switch, so a workspaces answer for the one before is dropped. */
  private workspacesAsked = 0;
  // Each list is a ReplaySubject holding the last project's value, so only a new emission says the new one has landed.
  private entitiesArrival = newArrival();
  private pagesArrival = newArrival();
  private workspacesArrival: Promise<void> = Promise.resolve();

  wire(): void {
    if (this.wired || environment.isRuntime) return;
    this.wired = true;

    this.entityService.$values.subscribe((entities: any[]) => {
      this.cachedEntities = entities || [];
      this.entitiesArrival.arrive();
    });
    this.pageService.$values.subscribe((pages: any[]) => {
      this.cachedPages = (pages || []).map((p: any) => ({ id: p.id, name: p.name }));
      this.pagesArrival.arrive();
    });
    // A sign-in fires clientChanged$ too. Until the new project's lists arrive, read_project answers
    // with nothing rather than with the last project's entities under the new project's id.
    this.auth.clientChanged$.subscribe(() => {
      this.confirmWaits.closeAll('the builder switched project');
      this.forgetProject();
      this.refreshWorkspaces();
    });
    this.auth.signOut$.subscribe(() => {
      this.confirmWaits.closeAll('the builder signed out');
      this.forgetProject();
    });
    this.workspacesService.changed$.subscribe(() => this.refreshWorkspaces());

    this.addTools();

    const bridge = this.registry.register('dev-local', SERVER_INSTRUCTIONS);
    if (!bridge) return;

    this.refreshWorkspaces();
    // Calls arrive from main outside Angular's zone; running them inside it lets navigation and the
    // highlight render.
    const NativePromise = nativePromise();
    bridge.onToolCall((call: ToolCall) => new NativePromise<ToolAnswer>((resolve, reject) => {
      this.zone.run(() => this.handleToolCall(call, bridge)).then(resolve, reject);
    }));
  }

  private addTools(): void {
    const selectedPage = () => {
      const p = this.pageService.selectedPage;
      return p?.id && p.id !== NO_PAGE_ID ? { id: p.id, name: p.name } : null;
    };

    const selectedEntity = () => {
      const page = this.entityPageBridge.page;
      if (!page) return null;
      const open = page.open();
      return open ? { id: open.id, name: open.name } : null;
    };
    const probes: WhereAmIProbes = {
      pageMode: () => this.pageMode(),
      settings: () => (this.panels.settings?.isOpen() ? { card: this.panels.settings.here().panel ?? null } : null),
      panels: () => (this.panels.current ? { screen: this.panels.current.screen, ...this.panels.current.here() } : null),
    };

    // Was (2026-10-30), when where_am_i asked only for the page and the entity, inline:
    // this.registry.add(makeWhereAmITool(
    //   () => {
    //     const p = this.pageService.selectedPage;
    //     return p?.id ? { id: p.id, name: p.name } : null;
    //   },
    //   () => {
    //     const page = this.entityPageBridge.page;
    //     if (!page) return null;
    //     const open = page.open();
    //     return open ? { id: open.id, name: open.name } : null;
    //   },
    // ));
    this.registry.add(makeWhereAmITool(selectedPage, selectedEntity, probes));

    this.registry.add(makeReadProjectTool(
      () => this.cachedEntities,
      () => this.cachedPages,
      () => this.cachedWorkspaces,
    ));

    const navigateDeps: NavigateDeps = {
      go: (screen: KnownScreen, id?: string) => this.navigateGo(screen, id),
      // Each screen's own measure: the entity page's count of unsaved changes, and the page editor's
      // leave guard, which Spotlight registers with the navigator for the same question.
      unsaved: () => {
        const current = screenFromUrl(this.router.url);
        if (current === 'entity') {
          const open = this.entityPageBridge.page?.open();
          if (!open) return null;
          const count = countUnsavedChanges(this.entityPageBridge.page!.openSaved(), open);
          return count > 0 ? { count, name: open.name || 'The new entity' } : null;
        }
        if (current === 'page-editor') return this.nav.unsavedHere();
        return this.panels.current?.unsaved?.() ?? null;
      },
      openHere: () => {
        const current = screenFromUrl(this.router.url);
        if (current === 'entity') return this.entityPageBridge.page?.open()?.id ?? null;
        if (current === 'page-editor' || current === 'page-data' || current === 'function-editor') return selectedPage()?.id ?? null;
        return null;
      },
      pageMode: () => this.pageMode(),
      settingsOpen: () => !!this.panels.settings?.isOpen(),
      closeSettings: () => this.panels.settings?.close(),
      settingsUnsaved: () => (this.panels.settings?.isOpen() ? this.panels.settings.unsaved?.() ?? null : null),
      selectPanel: (screen, panel) => this.selectPanel(screen, panel),
      whyPanelNotShown: (screen, panel) => this.panelsOf(screen)?.whyNot?.(panel) ?? null,
    };

    this.registry.add(makeNavigateTool(navigateDeps));

    this.registry.add(makeOpenProjectTool({
      accessibleProjects: () => this.nav.myProjects(),
      stillSigningIn: () => this.auth.stillSigningIn(),
      unsaved: navigateDeps.unsaved,
      settingsUnsaved: navigateDeps.settingsUnsaved,
      settingsOpen: navigateDeps.settingsOpen!,
      closeSettings: navigateDeps.closeSettings!,
      switchTo: (id) => this.switchProject(id),
      goToFirstScreen: () => this.nav.go({ kind: 'data' }, null),
    }));

    const behaviours: ActionBehaviours = {
      ...ACTION_BEHAVIOURS,
      ...makeEntityConfirmBehaviours({ bridge: this.entityPageBridge, waits: this.confirmWaits, highlight: this.highlightService }),
      ...makeOtherConfirmBehaviours({ waits: this.confirmWaits, highlight: this.highlightService, overlay: this.overlayConfirmations }),
      ...makeWorkspaceFormBehaviours({ waits: this.confirmWaits, highlight: this.highlightService, overlay: this.overlayConfirmations }),
    };
    this.registry.add(makeReadScreenTool({
      locate: (ctx) => locate(ctx, selectedPage, selectedEntity, probes),
      holds: (screen) => this.screenHolds(screen),
      loading: (screen) => !!this.panelsOf(screen)?.loading?.(),
      behaviours,
    }));
    this.registry.add(makeDoActionTool({ behaviours }));

    const showDeps: ShowDeps = {
      getPageComponentIds: () => {
        const p = this.pageService.selectedPage;
        if (!p?.sections) return null;
        const ids: string[] = [];
        for (const s of p.sections) collectComponentIds(s, ids);
        return ids.length > 0 ? ids : null;
      },
      getEntityFieldNames: () => {
        const page = this.entityPageBridge.page;
        if (!page) return null;
        const open = page.open();
        if (!open?.data) return null;
        return open.data.map((f: any) => f.name);
      },
      highlightService: this.highlightService,
      findElement: findTargetElement,
    };

    this.registry.add(makeShowTool(showDeps));

    const addFieldDeps: AddEntityFieldDeps = {
      waits: this.confirmWaits,
      highlight: this.highlightService,
      bridge: this.entityPageBridge,
      navigateDeps,
      getEntities: () => this.cachedEntities,
    };

    this.registry.add(makeAddEntityFieldTool(addFieldDeps));
  }

  private panelsOf(screen: string): ScreenPanels | null {
    if (screen === 'settings') return this.panels.settings;
    const current = this.panels.current;
    // The entity list and an open entity are one screen to the panels and two to the tools.
    return current?.screen === screen || (current?.screen === 'entity' && screen === 'data') ? current : null;
  }

  private screenHolds(screen: string): Record<string, unknown> | null {
    if (screen === 'settings') return this.panels.settings?.holds?.() ?? null;
    if (screen === 'workspaces' && this.panels.current?.screen === screen) {
      // The list lives in a child of the screen, while the wiring already keeps a copy for read_project.
      return { workspaces: this.cachedWorkspaces, ...this.panels.current.holds?.() };
    }
    const current = this.panels.current;
    // The entity list and an open entity are one screen to the panels and two to the tools.
    const same = current?.screen === screen || (current?.screen === 'entity' && screen === 'data');
    return same ? current?.holds?.() ?? null : null;
  }

  // Was (2026-10-30), before a screen could give its own refusal or take an id it doesn't need:
  // private async navigateGo(screen: KnownScreen, id?: string): Promise<boolean> {
  //   const navKind = SCREEN_TO_NAV[screen];
  //   if (!navKind) return false;
  //
  //   const needsObject = screen === 'entity' || screen === 'page-editor';
  //   if (needsObject && id) {
  //     const objectKind = screen === 'entity' ? 'entity' : 'page';
  //     const name = await this.resolveObjectName(objectKind, id);
  //     if (!name) return false;
  //     return this.nav.go({ kind: navKind, object: { kind: objectKind, id, name } }, null);
  //   }
  //
  //   return this.nav.go({ kind: navKind }, null);
  // }
  private async navigateGo(screen: KnownScreen, id?: string): Promise<boolean | { refused: string }> {
    if (screen === 'bastion') return this.goToBastion(id);

    if (screen === 'workspaces' && id) {
      return this.nav.go({ kind: 'workspace', object: { kind: 'workspace', id, name: id } }, null);
    }

    // The list and an open entity share a URL, so a route to it alone would leave the entity open.
    if (screen === 'data' && this.entityPageBridge.page?.open()) {
      await this.router.navigateByUrl('/blank', { skipLocationChange: true });
    }

    const navKind = SCREEN_TO_NAV[screen];
    if (!navKind) return false;

    const objectKind = OBJECT_KIND[screen];
    if (objectKind && id) {
      const name = await this.resolveObjectName(objectKind, id);
      if (!name) return false;
      return this.nav.go({ kind: navKind, object: { kind: objectKind, id, name } }, null);
    }

    return this.nav.go({ kind: navKind }, null);
  }

  private async goToBastion(flowId?: string): Promise<boolean | { refused: string }> {
    const onTeam = await this.auth.hasZavoTeamClaim().catch(() => false);
    if (!onTeam) return { refused: 'Bastion is open to the Zavo team only, and this builder isn\'t on it.' };
    return this.router.navigate(flowId ? ['/dashboard/bastion', flowId] : ['/dashboard/bastion']);
  }

  private pageMode(): PageMode | null {
    const editor = this.uiShared.uiEditor as { spotlightActive?: boolean } | null;
    if (!editor) return null;
    return editor.spotlightActive ? 'spotlight' : 'preview';
  }

  private async selectPanel(screen: KnownScreen, panel: string): Promise<boolean> {
    const until = Date.now() + PANEL_WAIT_MS;
    for (;;) {
      const target = screen === 'settings'
        ? this.panels.settings
        : this.panels.current?.screen === screen ? this.panels.current : null;
      if (target?.select(panel)) return true;
      if (Date.now() >= until) return false;
      await new Promise((r) => setTimeout(r, 100));
    }
  }

  /** Switches in place, then waits for the new project's lists, so the next call never sees the old ones or none. */
  private async switchProject(id: string): Promise<{ entities: number; pages: number; listsArrived: boolean } | null> {
    if (!(await this.nav.openProject(id))) return null;

    const listsArrived = await Promise.race([
      Promise.all([this.entitiesArrival.arrived, this.pagesArrival.arrived, this.workspacesArrival]).then(() => true),
      new Promise<boolean>((r) => setTimeout(() => r(false), LISTS_WAIT_MS)),
    ]);

    // Same-URL navigation does nothing, and the Data screen may hold an entity of the project just left.
    if (screenFromUrl(this.router.url) === 'entity') await this.router.navigateByUrl('/blank', { skipLocationChange: true });
    await this.nav.go({ kind: 'data' }, null);

    return { entities: this.cachedEntities.length, pages: this.cachedPages.length, listsArrived };
  }

  private async resolveObjectName(kind: 'entity' | 'page', id: string): Promise<string | null> {
    if (kind === 'entity') {
      const entities = await firstListOrEmpty(this.entityService);
      const e = entities.find((x: any) => x.id === id);
      return e ? e.name : null;
    }
    const pages = await firstListOrEmpty(this.pageService);
    const p = pages.find((x: any) => x.id === id);
    return p ? p.name : null;
  }

  private async handleToolCall(call: ToolCall, _bridge: ZavoBridge): Promise<ToolAnswer> {
    const tool = this.registry.get(call.tool);
    if (!tool) {
      return { outcome: 'refused', refusal: `Unknown tool '${call.tool}'. Tools: ${this.registry.names().join(', ')}.` };
    }

    // Held steps wait on the in-app card; the approval layer, not the tools, talks to main about asks.
    const askApproval = (ask: ApprovalAsk): Promise<ApprovalAnswer> => this.approval.ask(ask);

    const ctx = await this.registry.buildContext(call.request, askApproval);
    if (!ctx.projectId && !tool.worksWithoutProject) return { outcome: 'refused', refusal: NO_PROJECT_REFUSAL };

    // A malformed call would otherwise throw inside the tool and reach Claude as a bare error.
    const input = withoutCallFields(call.input);
    const problem = inputProblem(tool.inputSchema as Record<string, unknown> | undefined, input);
    if (problem) {
      return { outcome: 'refused', refusal: `${problem} Nothing was changed; call ${tool.name} again with that fixed.` };
    }
    return tool.run(input as Record<string, unknown>, ctx);
  }

  private forgetProject(): void {
    this.cachedEntities = [];
    this.cachedPages = [];
    this.cachedWorkspaces = [];
    this.workspacesAsked++;
    this.entitiesArrival = newArrival();
    this.pagesArrival = newArrival();
  }

  private refreshWorkspaces(): void {
    const asked = ++this.workspacesAsked;
    // Was (2026-10-30), before open_project had to wait for this list to arrive:
    // this.workspacesService.list().then((res) => {
    //   if (asked !== this.workspacesAsked) return;
    //   if (res?.ok) {
    //     this.cachedWorkspaces = (res.data.workspaces || []).map((w: any) => ({ id: w.id, name: w.name }));
    //   }
    // }).catch(() => {});
    this.workspacesArrival = this.workspacesService.list().then((res) => {
      if (asked !== this.workspacesAsked) return;
      if (res?.ok) {
        this.cachedWorkspaces = (res.data.workspaces || []).map((w: any) => ({ id: w.id, name: w.name }));
      }
    }).catch(() => {});
  }
}
